import { Type } from "@sinclair/typebox";
import { DraftStatus, SpiceLevel } from "../enums";
import { OPTION_TEXT_MAX } from "../limits";
/**
 * Admin contracts for the proposal table. plan.md §3.
 *
 * Drafts are never delivered to players — the game selects from `suggestions`,
 * a different table — so nothing here has a player-facing counterpart.
 */
export const DraftStatusSchema = Type.Enum(DraftStatus);
const BATCH = { minLength: 1, maxLength: 32 };
export const SuggestionDraftSchema = Type.Object({
    id: Type.String({ format: "uuid" }),
    batch: Type.String(),
    /** What review decided, and what promotion carries. Edited in place. */
    text: Type.String(),
    /**
     * What the author wrote. Never changes, so a rewrite stays readable as one.
     * Equal to `text` until review edits it; null only on rows predating the
     * column.
     */
    proposed_text: Type.Union([Type.String(), Type.Null()]),
    /** What the author claimed. Never changes, so drift stays measurable. */
    proposed_spice: Type.Union([Type.Enum(SpiceLevel), Type.Null()]),
    /** What review decided. Equal to proposed_spice until someone moves it. */
    spice: Type.Enum(SpiceLevel),
    locale: Type.String(),
    pack: Type.String(),
    status: DraftStatusSchema,
    review_note: Type.Union([Type.String(), Type.Null()]),
    reviewed_at: Type.Union([Type.String(), Type.Null()]),
    promoted_at: Type.Union([Type.String(), Type.Null()]),
    created_at: Type.String(),
});
/** One proposal as written, before the database assigns anything. */
export const DraftInputSchema = Type.Object({
    text: Type.String({ minLength: 1, maxLength: OPTION_TEXT_MAX }),
    spice: Type.Enum(SpiceLevel),
    locale: Type.Optional(Type.String({ minLength: 1, maxLength: 16 })),
    pack: Type.Optional(Type.String({ minLength: 1, maxLength: 32 })),
});
/** Bulk import of one batch. Duplicates are skipped, not fatal. */
export const DraftImportReqSchema = Type.Object({
    batch: Type.String(BATCH),
    drafts: Type.Array(DraftInputSchema, { minItems: 1 }),
});
export const DraftImportResSchema = Type.Object({
    inserted: Type.Integer(),
    skipped: Type.Integer(),
});
export const DraftListReqSchema = Type.Object({
    batch: Type.Optional(Type.String(BATCH)),
    spice: Type.Optional(Type.Enum(SpiceLevel)),
    status: Type.Optional(DraftStatusSchema),
    search: Type.Optional(Type.String()),
    limit: Type.Optional(Type.Integer({ minimum: 1, maximum: 500 })),
    offset: Type.Optional(Type.Integer({ minimum: 0 })),
});
export const DraftListResSchema = Type.Object({
    drafts: Type.Array(SuggestionDraftSchema),
    total: Type.Integer(),
    /** Every batch label present, newest first, so the filter can offer them. */
    batches: Type.Array(Type.String()),
});
/**
 * One review decision. Every field but the id is optional because the four
 * things a reviewer does — accept, reject with a reason, move the spice, rewrite
 * the line — are independent, and none of them may force a verdict alongside.
 */
export const DraftReviewReqSchema = Type.Object({
    id: Type.String({ format: "uuid" }),
    /** Replaces the line. `proposed_text` keeps what it was. */
    text: Type.Optional(Type.String({ minLength: 1, maxLength: OPTION_TEXT_MAX })),
    status: Type.Optional(DraftStatusSchema),
    spice: Type.Optional(Type.Enum(SpiceLevel)),
    review_note: Type.Optional(Type.Union([Type.String({ maxLength: 500 }), Type.Null()])),
});
export const DraftReviewResSchema = SuggestionDraftSchema;
/** Copies accepted drafts into the live library. */
export const DraftPromoteReqSchema = Type.Object({
    /** Restrict to one batch. Omitted promotes every accepted draft. */
    batch: Type.Optional(Type.String(BATCH)),
});
export const DraftPromoteResSchema = Type.Object({
    promoted: Type.Integer(),
    /** Accepted drafts whose text already exists live. Reported, not duplicated. */
    skipped: Type.Integer(),
});
/**
 * How one batch fared, per spice level. The reason the table exists: it shows
 * where a level was overrated, not merely how much changed.
 */
export const DraftBatchStatSchema = Type.Object({
    batch: Type.String(),
    proposed_spice: Type.Enum(SpiceLevel),
    total: Type.Integer(),
    accepted: Type.Integer(),
    rejected: Type.Integer(),
    pending: Type.Integer(),
    /** Accepted drafts whose spice review left alone. */
    kept_spice: Type.Integer(),
    moved_down: Type.Integer(),
    moved_up: Type.Integer(),
});
export const DraftStatsResSchema = Type.Object({
    stats: Type.Array(DraftBatchStatSchema),
});
